import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import WebsiteLayout from '../components/WebsiteLayout';
import Cite from '../components/Cite';
import { useLanguage } from '../../../hooks/useLanguage';

const ResearchLimit: React.FC = () => {
  const [language] = useLanguage();
  const en = language === 'en';

  const customCta = (
    <div className="site-end__cta">
      <span className="site-end__label">{en ? 'Talk to us' : 'Praat met ons'}</span>
      <h2 className="site-end__title">
        {en
          ? "Working on something where the substrate matters? Let's compare notes."
          : 'Werk je aan iets waarbij de AI-laag ertoe doet? Laten we kennis vergelijken.'}
      </h2>
      <div className="site-end__action">
        <Link className="btn btn-primary" to="/contact">
          {en ? 'Contact us' : 'Neem contact op'}
        </Link>
      </div>
    </div>
  );

  return (
    <WebsiteLayout customCta={customCta}>
      <Helmet>
        <title>
          {en
            ? 'It was never about how many dimensions · Thoughtful Oasis'
            : 'Het ging nooit over hoeveel dimensies · Thoughtful Oasis'}
        </title>
        <meta
          name="description"
          content="Not dimension but axis-domain alignment governs retrieval. A 2026 counter-example to the LIMIT sphere-packing bound."
        />
        <link rel="canonical" href="https://thoughtful-oasis.com/research/limit" />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Thoughtful Oasis" />
        <meta
          property="og:title"
          content={
            en
              ? 'It was never about how many dimensions'
              : 'Het ging nooit over hoeveel dimensies'
          }
        />
        <meta
          property="og:description"
          content="Not dimension but axis-domain alignment governs retrieval. A 2026 counter-example to the LIMIT sphere-packing bound."
        />
        <meta property="og:url" content="https://thoughtful-oasis.com/research/limit" />
        <meta property="og:image" content="https://thoughtful-oasis.com/assets/website/photos/og-home.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={
            en
              ? 'It was never about how many dimensions'
              : 'Het ging nooit over hoeveel dimensies'
          }
        />
        <meta
          name="twitter:description"
          content="Not dimension but axis-domain alignment governs retrieval. A 2026 counter-example to the LIMIT sphere-packing bound."
        />
        <meta name="twitter:image" content="https://thoughtful-oasis.com/assets/website/photos/og-home.jpg" />
        <script type="application/ld+json">{`{
          "@context": "https://schema.org",
          "@type": "ScholarlyArticle",
          "headline": "It was never about how many dimensions. It was always about which ones.",
          "author": [
            {"@type": "Person", "name": "Kaspar Rothenfusser"},
            {"@type": "Person", "name": "Bekk Blando"}
          ],
          "datePublished": "2026",
          "publisher": {"@type": "Organization", "name": "Thoughtful Oasis", "url": "https://thoughtful-oasis.com/"},
          "url": "https://thoughtful-oasis.com/research/limit"
        }`}</script>
        <script type="application/ld+json">{`{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {"@type": "ListItem", "position": 1, "name": "Research", "item": "https://thoughtful-oasis.com/research"},
            {"@type": "ListItem", "position": 2, "name": "LIMIT counter-example", "item": "https://thoughtful-oasis.com/research/limit"}
          ]
        }`}</script>
      </Helmet>

      <section className="post">
        <div className="post-layout">
          <aside className="post-aside">
            <p className="post-aside__byline">
              {en ? (
                <>
                  Kaspar Rothenfusser &amp; Bekk Blando, 2026 ·{' '}
                  <a
                    className="link-quiet"
                    href="/limit.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Read preprint
                  </a>{' '}
                  ·{' '}
                  <a
                    className="link-quiet"
                    href="mailto:kaspar@thoughtful-oasis.com?subject=LIMIT%20counter-example%20code"
                  >
                    Code on request
                  </a>{' '}
                  ·{' '}
                  <Link className="link-quiet" to="/research">
                    All research
                  </Link>
                </>
              ) : (
                <>
                  Kaspar Rothenfusser &amp; Bekk Blando, 2026 ·{' '}
                  <a
                    className="link-quiet"
                    href="/limit.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Preprint lezen
                  </a>{' '}
                  ·{' '}
                  <a
                    className="link-quiet"
                    href="mailto:kaspar@thoughtful-oasis.com?subject=LIMIT%20counter-example%20code"
                  >
                    Code op aanvraag
                  </a>{' '}
                  ·{' '}
                  <Link className="link-quiet" to="/research">
                    Al het onderzoek
                  </Link>
                </>
              )}
            </p>
            <Cite
              filename="rothenfusser-blando-2026-limit"
              apa="Rothenfusser, K., & Blando, B. (2026). Domain-property alignment in single-vector retrieval [Preprint]. Thoughtful Oasis."
              bibtex={'@unpublished{rothenfusser2026limit,\n  author = {Rothenfusser, Kaspar and Blando, Bekk},\n  title  = {Domain-Property Alignment in Single-Vector Retrieval},\n  year   = {2026},\n  note   = {Preprint, Thoughtful Oasis}\n}'}
            />
          </aside>
          <article className="post__main">
            <p className="post__meta">
              {en
                ? 'Plain-language summary · Empirical · 2026'
                : 'Samenvatting in gewone taal · Empirisch · 2026'}
            </p>
            <h1>
              {en
                ? 'It was never about how many dimensions. It was always about which ones.'
                : 'Het ging nooit over hoeveel dimensies. Het ging altijd over welke.'}
            </h1>
            <p className="post__lede">
              {en
                ? 'A recent proof established a hard floor on embedding dimension for retrieval. This paper shows it answers the wrong question. What determines retrieval is not how many dimensions — it\'s whether the axes align with the domain properties queries address.'
                : 'Een recent bewijs stelde een harde ondergrens vast voor embedding-dimensie bij retrieval. Deze paper laat zien dat het de verkeerde vraag beantwoordt. Wat retrieval bepaalt is niet hoeveel dimensies — maar of de assen overeenkomen met de domeineigenschappen waar queries naar vragen.'}
            </p>

            {en ? (
              <div className="post__body" lang="en">
                <p>
                  Weller et al. (2025) proved a sphere-packing lower bound on the dimension needed for
                  embedding-based retrieval: to host every possible k-subset of n documents as a separable
                  region at margin γ, you need <code>d ≥ log(n choose k) / log(1 + 1/γ)</code>. On LIMIT —
                  the benchmark they built to make this bound bite — state-of-the-art dense retrievers scored
                  recall@2 below <strong>0.08</strong> (the two OpenAI models at 0.019 and 0.016). The
                  reading was that single-vector retrieval is fundamentally constrained by dimension.
                </p>
                <p>This paper shows that the bound, while correct, captures a different question than the one retrieval asks.</p>

                <h2>Two different questions</h2>
                <p>
                  The sphere-packing bound counts how many separable regions <em>R<sup>d</sup></em> can host.
                  Real retrieval doesn't need to host arbitrary subsets. Queries are domain predicates — <em>which
                  contracts mention force majeure</em>, <em>who likes apples</em> — and the realisable gold
                  sets are the ones those predicates select. The two questions coincide only when queries can
                  pick out arbitrary subsets. On any domain-tied retrieval task they cannot.
                </p>

                <h2>A small example</h2>
                <p>The paper illustrates the gap with six documents and three binary properties:</p>
                <table className="post__table">
                  <thead>
                    <tr>
                      <th></th>
                      <th>colour</th>
                      <th>sleeve</th>
                      <th>type</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Doc 1</td>
                      <td>red</td>
                      <td>long</td>
                      <td>shirt</td>
                    </tr>
                    <tr>
                      <td>Doc 2</td>
                      <td>red</td>
                      <td>long</td>
                      <td>pants</td>
                    </tr>
                    <tr>
                      <td>Doc 3</td>
                      <td>red</td>
                      <td>short</td>
                      <td>shirt</td>
                    </tr>
                    <tr>
                      <td>Doc 4</td>
                      <td>red</td>
                      <td>short</td>
                      <td>pants</td>
                    </tr>
                    <tr>
                      <td>Doc 5</td>
                      <td>blue</td>
                      <td>long</td>
                      <td>shirt</td>
                    </tr>
                    <tr>
                      <td>Doc 6</td>
                      <td>blue</td>
                      <td>long</td>
                      <td>pants</td>
                    </tr>
                  </tbody>
                </table>
                <p>
                  Here the two ceilings point in opposite directions, and this is the case where alignment
                  lands <em>below</em> sphere-packing. Hosting any k=3 subset as a separable region at γ=1
                  demands d ≥ 5. But the corpus carries only three binary dichotomies — colour, sleeve, type
                  — and nothing more. Place each document on the cube along those three axes and every
                  atomic query is exact. There is no fourth direction the documents could inform, because
                  there is no fourth property. The extra dimensions sphere-packing asks for can only be
                  filled by baking specific compound queries into the geometry at training time —
                  query-informed axes, not anything the documents contain. The alignment ceiling says three,
                  one per property.
                </p>

                <h2>A corpus-size-independent ceiling</h2>
                <p>
                  The paper gives the general version. Let P be the number of domain properties. Embed each
                  document as its property indicator (a P-dimensional binary vector), each query as the one-hot
                  vector for the property it asks about. The inner product returns 1 for relevant documents
                  and 0 otherwise — exact retrieval, regardless of corpus size. <code>d ≤ |P|</code> always
                  suffices.
                </p>
                <p>
                  For LIMIT, <code>|P| = 1848</code>. Here the gap runs the other way: sphere-packing at
                  N=50,000, k=2, γ=1 predicts only d ≳ 30, while the alignment ceiling is d ≤ |P| = 1848.
                  The sphere-packing number is small because it allows the geometry to overfit one specific
                  qrel matrix; the alignment ceiling is what an encoder needs to handle arbitrary corpora
                  and queries over the same vocabulary without re-fitting. SOTA dense retrievers sit at
                  d ∈ [384, 3072] — well above the sphere-packing prediction — and still fail.
                </p>

                <h2>Results on LIMIT</h2>
                <p>
                  One-hot property basis at d=1848: <strong>recall@2 = 1.000</strong>. No learning, roughly
                  300 lines of Python, five minutes on a laptop CPU.
                </p>
                <p>For comparison, the dense retrievers LIMIT was built to test:</p>
                <ul>
                  <li><code>all-MiniLM-L6-v2</code> at d=384: below 0.08</li>
                  <li><code>all-mpnet-base-v2</code> at d=768: below 0.08</li>
                  <li><code>text-embedding-3-small</code> at d=1536: recall@2 = 0.019</li>
                  <li><code>text-embedding-3-large</code> at d=3072: recall@2 = 0.016</li>
                </ul>
                <figure className="post__figure">
                  <img
                    src="/assets/website/photos/papers/recall-vs-k.webp"
                    alt="Recall@k on LIMIT (50k documents). The one-hot property basis saturates at 1.000 from k=2. Dense retrievers stay below 0.08."
                    loading="lazy"
                  />
                  <figcaption>
                    Recall@k on the full 50,000-document LIMIT corpus. The property basis (dark blue) saturates
                    at 1.000 from k=2. Dense retrievers (red/magenta) stay near zero regardless of k.
                  </figcaption>
                </figure>
                <p>
                  A Gaussian random projection of the property basis to d=1024 reaches recall@2 ={' '}
                  <strong>0.772</strong>. The matrix is unstructured — no domain knowledge, just a random
                  rotation of the column space, which it preserves up to that rotation. An SVD truncation
                  at d=2 already scores <strong>0.041</strong> — higher than every dense retriever in the
                  comparison, including <code>text-embedding-3-large</code> at d=3072.
                </p>
                <figure className="post__figure">
                  <img
                    src="/assets/website/photos/papers/recall-vs-dim.webp"
                    alt="Recall@2 vs embedding dimension on LIMIT. SVD of the property basis climbs with dimension and beats all dense retrievers everywhere. The one-hot property basis at d=1848 reaches 1.000."
                    loading="lazy"
                  />
                  <figcaption>
                    Recall@2 vs. embedding dimension. The dense retrievers (red/magenta) sit near zero
                    regardless of d. SVD of the property basis (dark green) is above every dense retriever
                    everywhere; the one-hot basis (dark blue, d=1848) reaches 1.000.
                  </figcaption>
                </figure>
                <blockquote>
                  The encoders are not failing because they cannot represent enough things. They are failing
                  because their embedding space represents the wrong things.
                </blockquote>

                <h2>Compound queries become algebra</h2>
                <p>
                  When the basis is interpretable — each axis is a named property, as in a{' '}
                  <Link className="link-inline" to="/research/vector-ontologies">
                    vector ontology
                  </Link>{' '}
                  — every Boolean compound query becomes a single vector in the same <em>R<sup>P</sup></em>.
                  AND is the sum of one-hot vectors (select where score = n). OR is the same sum (select where
                  score ≥ 1). NOT is a signed difference. No re-ranking, no query decomposition: composition
                  is arithmetic on the basis.
                </p>
                <p>
                  On 174 random property pairs the property basis is exact by construction. Dense retrievers
                  score ≤ 0.025 regardless of whether they receive natural-language compounds or pre-parsed
                  Boolean structure. Parsing is not the bottleneck — composition fails in the embedding.
                </p>

                <h2>Why dense retrievers fail</h2>
                <p>
                  Two attributes — not dimension — explain the failure. Contrastive sentence-similarity
                  training writes <em>language</em> directions onto the axes (synonymy, register, topical
                  proximity), not property indicators. The basis is rotated away from the relevance signal.{' '}
                  <code>text-embedding-3-large</code> at d=3072 has nearly twice as many dimensions as the
                  property basis and scores <em>lower</em> than <code>text-embedding-3-small</code> at d=1536.
                  What is missing is alignment, not capacity.
                </p>
                <p>
                  And opacity prevents vector arithmetic: there are no named axes to combine, no stable
                  operations for AND/OR/NOT. Modern retrieval acknowledges this in practice — query
                  decomposition gets delegated to an LLM, or the system switches to multi-vector
                  representations. When the basis itself carries the structure, the workaround is unnecessary.
                </p>

                <h2>The open question</h2>
                <p>
                  On LIMIT the property vocabulary is recoverable by string matching — it's gifted by the
                  dataset's construction. A generic corpus doesn't ship with that. The paper identifies three
                  routes: controlled-vocabulary domains (ICD-10, patent classifications, product schemas),
                  LLM-driven property extraction (building on the{' '}
                  <Link className="link-inline" to="/research/worldview">
                    prior worldview paper
                  </Link>
                  ), and domain finetuning of a small encoder.
                </p>
                <p>
                  The random-projection result narrows the target: exact canonical axes are not required for
                  atomic retrieval — any basis spanning the right column space suffices. Interpretable axes
                  are required for the compound arithmetic. Concurrent work by Archish et al. (2026) shows
                  that finetuning closes the atomic gap on LIMIT, consistent with this paper's account:
                  finetuning warps a generic basis toward the task's property axes. Their setup does not test
                  compositional generalisation.
                </p>
                <p>
                  Technical details, recall-vs-dimension curves, and compound-query comparisons are in the
                  full paper. The companion code runs the whole benchmark in about seven minutes on a laptop
                  CPU; no GPU, no API key for the open baselines.
                </p>
              </div>
            ) : (
              <div className="post__body" lang="nl">
                <p>
                  Weller et al. (2025) bewezen een sphere-packing-ondergrens voor de dimensie die nodig is
                  voor embedding-gebaseerde retrieval: om elke mogelijke k-deelverzameling van n documenten
                  als scheidbaar gebied met marge γ te huisvesten, heb je{' '}
                  <code>d ≥ log(n choose k) / log(1 + 1/γ)</code> nodig. Op LIMIT — de benchmark die ze
                  bouwden om die grens te laten bijten — scoorden state-of-the-art dense retrievers recall@2
                  onder <strong>0,08</strong> (de twee OpenAI-modellen op 0,019 en 0,016). De lezing was
                  dat single-vector retrieval fundamenteel beperkt is door dimensie.
                </p>
                <p>Deze paper laat zien dat de grens, hoewel correct, een andere vraag beantwoordt dan die retrieval stelt.</p>

                <h2>Twee verschillende vragen</h2>
                <p>
                  De sphere-packing-grens telt hoeveel scheidbare gebieden <em>R<sup>d</sup></em> kan
                  bevatten. Echte retrieval hoeft geen willekeurige deelverzamelingen te herbergen. Queries
                  zijn domein-predicaten — <em>welke contracten noemen overmacht</em>, <em>wie houdt van
                  appels</em> — en de realiseerbare gold sets zijn degene die die predicaten selecteren. De
                  twee vragen vallen alleen samen als queries willekeurige deelverzamelingen kunnen
                  aanwijzen. Op elke domein-gebonden taak kan dat niet.
                </p>

                <h2>Een klein voorbeeld</h2>
                <p>De paper illustreert het verschil met zes documenten en drie binaire eigenschappen:</p>
                <table className="post__table">
                  <thead>
                    <tr>
                      <th></th>
                      <th>kleur</th>
                      <th>mouw</th>
                      <th>type</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Doc 1</td>
                      <td>rood</td>
                      <td>lang</td>
                      <td>shirt</td>
                    </tr>
                    <tr>
                      <td>Doc 2</td>
                      <td>rood</td>
                      <td>lang</td>
                      <td>broek</td>
                    </tr>
                    <tr>
                      <td>Doc 3</td>
                      <td>rood</td>
                      <td>kort</td>
                      <td>shirt</td>
                    </tr>
                    <tr>
                      <td>Doc 4</td>
                      <td>rood</td>
                      <td>kort</td>
                      <td>broek</td>
                    </tr>
                    <tr>
                      <td>Doc 5</td>
                      <td>blauw</td>
                      <td>lang</td>
                      <td>shirt</td>
                    </tr>
                    <tr>
                      <td>Doc 6</td>
                      <td>blauw</td>
                      <td>lang</td>
                      <td>broek</td>
                    </tr>
                  </tbody>
                </table>
                <p>
                  Hier wijzen de twee plafonds in tegengestelde richting, en dit is het geval waar alignment{' '}
                  <em>onder</em> sphere-packing ligt. Om een willekeurige k=3-deelverzameling als scheidbaar
                  gebied met γ=1 te huisvesten is d ≥ 5 nodig. Maar het corpus draagt slechts drie binaire
                  dichotomieën — kleur, mouw, type — en niets meer. Plaats elk document op de kubus langs die
                  drie assen en elke atomaire query is exact. Er is geen vierde richting die de documenten
                  kunnen informeren, want er is geen vierde eigenschap. De extra dimensies die sphere-packing
                  vraagt kunnen alleen gevuld worden door specifieke samengestelde queries op trainingsmoment
                  in de geometrie te bakken — query-geïnformeerde assen, niet iets wat de documenten zelf
                  bevatten. Het alignment-plafond zegt drie, één per eigenschap.
                </p>

                <h2>Een corpus-onafhankelijk plafond</h2>
                <p>
                  De paper geeft de algemene versie. Laat P het aantal domeineigenschappen zijn. Codeer elk
                  document als zijn eigenschap-indicator (een P-dimensionale binaire vector), elke query als
                  de one-hot-vector voor de eigenschap waarnaar gevraagd wordt. Het inproduct retourneert 1
                  voor relevante documenten en 0 voor de rest — exacte retrieval, ongeacht de
                  corpus-grootte. <code>d ≤ |P|</code> is altijd voldoende.
                </p>
                <p>
                  Voor LIMIT is <code>|P| = 1848</code>. Hier loopt het gat de andere kant op: sphere-packing
                  bij N=50.000, k=2, γ=1 voorspelt slechts d ≳ 30, terwijl het alignment-plafond
                  d ≤ |P| = 1848 is. Het sphere-packing-getal is klein omdat het de geometrie laat overfitten
                  op één specifieke qrel-matrix; het alignment-plafond is wat een encoder nodig heeft om
                  willekeurige corpora en queries over hetzelfde vocabulaire aan te kunnen zonder opnieuw te
                  fitten. SOTA dense retrievers zitten op d ∈ [384, 3072] — ruim boven de
                  sphere-packing-voorspelling — en falen alsnog.
                </p>

                <h2>Resultaten op LIMIT</h2>
                <p>
                  One-hot eigenschap-basis op d=1848: <strong>recall@2 = 1,000</strong>. Geen learning, circa
                  300 regels Python, vijf minuten op een laptop-CPU.
                </p>
                <p>Ter vergelijking, de dense retrievers waarvoor LIMIT gebouwd was:</p>
                <ul>
                  <li><code>all-MiniLM-L6-v2</code> op d=384: onder 0,08</li>
                  <li><code>all-mpnet-base-v2</code> op d=768: onder 0,08</li>
                  <li><code>text-embedding-3-small</code> op d=1536: recall@2 = 0,019</li>
                  <li><code>text-embedding-3-large</code> op d=3072: recall@2 = 0,016</li>
                </ul>
                <figure className="post__figure">
                  <img
                    src="/assets/website/photos/papers/recall-vs-k.webp"
                    alt="Recall@k op LIMIT (50k documenten). De one-hot eigenschap-basis verzadigt op 1,000 vanaf k=2. Dense retrievers blijven onder 0,08."
                    loading="lazy"
                  />
                  <figcaption>
                    Recall@k op het volledige LIMIT-corpus van 50.000 documenten. De eigenschap-basis
                    (donkerblauw) verzadigt op 1,000 vanaf k=2. Dense retrievers (rood/magenta) blijven bij
                    elke k dicht bij nul.
                  </figcaption>
                </figure>
                <p>
                  Een Gaussiaanse random projectie van de eigenschap-basis naar d=1024: recall@2 ={' '}
                  <strong>0,772</strong>. De matrix is ongestructureerd — geen domeinkennis, enkel een random
                  rotatie van de kolomruimte, die behouden blijft op die rotatie na. SVD-truncatie op d=2
                  scoort al <strong>0,041</strong> — nog steeds hoger dan elke dense retriever in de
                  vergelijking, inclusief <code>text-embedding-3-large</code> op d=3072.
                </p>
                <figure className="post__figure">
                  <img
                    src="/assets/website/photos/papers/recall-vs-dim.webp"
                    alt="Recall@2 vs. embedding-dimensie op LIMIT. SVD van de eigenschap-basis stijgt met dimensie en verslaat alle dense retrievers overal. De one-hot eigenschap-basis op d=1848 bereikt 1,000."
                    loading="lazy"
                  />
                  <figcaption>
                    Recall@2 vs. embedding-dimensie. Dense retrievers (rood/magenta) blijven dicht bij nul
                    ongeacht d. SVD van de eigenschap-basis (donkergroen) ligt overal boven elke dense
                    retriever; de one-hot basis (donkerblauw, d=1848) bereikt 1,000.
                  </figcaption>
                </figure>
                <blockquote>
                  De encoders falen niet omdat ze te weinig kunnen representeren. Ze falen omdat hun
                  embedding-ruimte de verkeerde dingen representeert.
                </blockquote>

                <h2>Samengestelde queries worden algebra</h2>
                <p>
                  Wanneer de basis interpreteerbaar is — elke as is een benoemde eigenschap, zoals in een{' '}
                  <Link className="link-inline" to="/research/vector-ontologies">
                    vector-ontologie
                  </Link>{' '}
                  — wordt elke Booleaanse samengestelde query een enkele vector in dezelfde{' '}
                  <em>R<sup>P</sup></em>. AND is de som van one-hot-vectoren (selecteer waar score = n). OR
                  is dezelfde som (selecteer waar score ≥ 1). NOT is een getekend verschil. Geen re-ranking,
                  geen query-decompositie: compositie is rekenkunde op de basis.
                </p>
                <p>
                  Op 174 willekeurige eigenschap-paren is de eigenschap-basis exact per constructie. Dense
                  retrievers scoren ≤ 0,025, ongeacht of ze natuurlijke-taal-samenstellingen of voorgeparsde
                  Booleaanse structuur krijgen. Parsing is niet de bottleneck — compositie faalt in de
                  embedding.
                </p>

                <h2>Waarom dense retrievers falen</h2>
                <p>
                  Twee kenmerken — niet dimensie — verklaren het falen. Contrastieve
                  zin-similariteitstraining schrijft <em>taal</em>richtingen op de assen (synoniemen,
                  register, thematische nabijheid), geen eigenschap-indicatoren. De basis staat weggeroteerd
                  van het relevantie-signaal. <code>text-embedding-3-large</code> op d=3072 heeft bijna twee
                  keer zoveel dimensies als de eigenschap-basis en scoort <em>lager</em> dan{' '}
                  <code>text-embedding-3-small</code> op d=1536. Wat ontbreekt is alignment, niet capaciteit.
                </p>
                <p>
                  En ondoorzichtigheid verhindert vector-rekenkunde: er zijn geen benoemde assen om te
                  combineren, geen stabiele operaties voor AND/OR/NOT. Moderne retrieval erkent dit in de
                  praktijk — query-decompositie wordt gedelegeerd aan een LLM, of het systeem schakelt over
                  naar multi-vector-representaties. Wanneer de basis zelf de structuur draagt, is de
                  workaround overbodig.
                </p>

                <h2>De open vraag</h2>
                <p>
                  Op LIMIT is het eigenschap-vocabulaire terug te vinden via string-matching — het is cadeau
                  gedaan door de constructie van de dataset. Een generiek corpus komt daar niet mee. De paper
                  identificeert drie routes: gecontroleerd-vocabulaire-domeinen (ICD-10,
                  octrooiclassificaties, product-schema's), LLM-gedreven eigenschap-extractie (voortbouwend
                  op de{' '}
                  <Link className="link-inline" to="/research/worldview">
                    eerdere worldview-paper
                  </Link>
                  ), en domein-finetuning van een kleine encoder.
                </p>
                <p>
                  Het random-projectie-resultaat vernauwt het doel: exacte canonieke assen zijn niet nodig
                  voor atomaire retrieval — elke basis die de juiste kolomruimte opspant volstaat.
                  Interpreteerbare assen zijn nodig voor de samengestelde rekenkunde. Gelijktijdig werk van
                  Archish et al. (2026) laat zien dat finetuning het atomaire gat op LIMIT dicht, in lijn met
                  de analyse van deze paper: finetuning buigt een generieke basis richting de eigenschap-assen
                  van de taak. Hun opzet test geen compositionele generalisatie.
                </p>
                <p>
                  Technische details, recall-vs-dimensie-curves en samengestelde-query-vergelijkingen staan
                  in de volledige paper. De bijbehorende code draait de hele benchmark in circa zeven minuten
                  op een laptop-CPU; geen GPU, geen API-key voor de open baselines.
                </p>
              </div>
            )}
          </article>
        </div>
      </section>
    </WebsiteLayout>
  );
};

export default ResearchLimit;
